import React from 'react'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Progress } from '../ui/progress'
import { Badge } from '../ui/badge'
import { TrendingUp, ArrowLeft, ArrowRight } from 'lucide-react'
import { Brand } from '../../utils/mockData'
import { TOOLBOX_CATEGORIES, TOOLBOX_ITEMS } from './brandDashboardData'

interface ProgressOverviewProps {
  brand: Brand
  isExpanded: boolean
  onToggleExpanded: () => void
}

export function ProgressOverview({ brand, isExpanded, onToggleExpanded }: ProgressOverviewProps) {
  const completedTools = TOOLBOX_ITEMS.filter(item => item.status === 'complete').length
  
  // Use brand's actual progress if available, otherwise calculate from toolbox items
  const brandProgress = typeof brand.overall_progress === 'number' ? brand.overall_progress : 0
  const toolboxProgress = Math.round(TOOLBOX_ITEMS.reduce((acc, item) => acc + item.progress, 0) / TOOLBOX_ITEMS.length)
  const overallProgress = brandProgress > 0 ? brandProgress : toolboxProgress

  return (
    <Card className="mb-8 bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <CardTitle className="flex items-center gap-2 text-blue-800">
              <TrendingUp className="w-5 h-5" />
              Overall Brand Progress
            </CardTitle>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-3xl font-bold text-blue-800">{overallProgress}%</div>
              {isExpanded && (
                <p className="text-sm text-blue-600">{completedTools} of {TOOLBOX_ITEMS.length} tools completed</p>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleExpanded}
              className="text-blue-600 hover:text-blue-700 hover:bg-blue-100"
            >
              {isExpanded ? (
                <>
                  <ArrowLeft className="w-4 h-4 mr-1 rotate-90" />
                  Less
                </>
              ) : (
                <>
                  <ArrowRight className="w-4 h-4 mr-1 rotate-90" />
                  More
                </>
              )}
            </Button>
          </div>
        </div>
      </CardHeader>
      {!isExpanded ? (
        <CardContent className="pt-0">
          <Progress value={overallProgress} className="h-2 bg-blue-100" />
        </CardContent>
      ) : (
        <CardContent className="pt-0">
          <div className="space-y-4">
            <CardDescription className="text-blue-600">
              Track your journey from discovery to launch
            </CardDescription>
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-blue-700">Completion Progress</span>
              <span className="text-blue-600">{overallProgress}% complete</span>
            </div>
            <Progress value={overallProgress} className="h-3 bg-blue-100" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-2">
              {TOOLBOX_CATEGORIES.slice(0, 4).map((category) => (
                <div key={category.name} className="text-center">
                  <div className="text-xs font-medium text-slate-600 mb-1">{category.name}</div>
                  <div className="text-sm font-bold text-blue-700">{category.progress}%</div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  )
}